import { removeNilOrEmptyObj } from '@/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import { omit } from 'ramda';

import {
	formatApplicantAssignmentAutocomplete,
	formatEnablePublicApplicants,
	formatFranchiseAssignmentAutocomplete,
	formatPublicAssignments,
	formatPublicListWithDate,
} from './pa2-01-assignment-calendar.helper';
import { AssignmentCalendarActions } from './pa2-01-assignment-calendar.redux';
import {
	IPayloadGetApplicantAssignmentAutocompleteRequest,
	IPayloadGetEnablePublicApplicantsRequest,
	IPayloadGetFranchiseAssignmentAutocompleteRequest,
	IPayloadPublicListWithDateRequest,
} from './pa2-01-assignment-calendar.type';

export function* getPublicListWithDateSaga(
	action: PayloadAction<IPayloadPublicListWithDateRequest>
) {
	const { callback, params } = action.payload;

	try {
		const { searchValue, searchType } = params;

		const res: API.ApiResponseSuccess<API.TGetPublicListWithDateResponse> =
			yield API.getPublicListWithDate({
				...removeNilOrEmptyObj(
					omit(['searchValue', 'searchType', 'currentMonth'], params)
				),
				searchId: searchValue,
				...(searchValue ? { searchType } : {}),
			});

		const data = formatPublicListWithDate(res?.data);

		yield put(
			AssignmentCalendarActions.getPublicListWithDateSuccess({
				data,
			})
		);

		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(AssignmentCalendarActions.getPublicListWithDateFailure());
	}
}

export function* getEnablePublicApplicants(
	action: PayloadAction<IPayloadGetEnablePublicApplicantsRequest>
) {
	const { callback, params } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TGetEnablePublicApplicantsResponse> =
			yield API.getEnablePublicApplicants(params);

		const data = formatEnablePublicApplicants(res?.data);

		yield put(AssignmentCalendarActions.getEnablePublicApplicantsSuccess(data));

		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(AssignmentCalendarActions.getEnablePublicApplicantsFailure());
	}
}

export function* getPublicAssignment(
	action: PayloadAction<IPayloadPublicListWithDateRequest>
) {
	const { callback, params } = action.payload;

	try {
		// call api
		const { publicMainFranchiseId, searchValue, searchType, programId } =
			params;

		const apiParams = removeNilOrEmptyObj({
			publicMainFranchiseId,
			searchId: searchValue,
			programId,
			...(searchValue ? { searchType } : {}),
		});

		const res: API.ApiResponseSuccess<API.TGetPublicAssignmentsResponse> =
			yield API.getPublicAssignments(apiParams);
		const data = formatPublicAssignments(res?.data);

		yield put(
			AssignmentCalendarActions.getPublicAssignmentSuccess({
				assignments: data,
			})
		);

		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(AssignmentCalendarActions.getPublicAssignmentFailure());
	}
}

export function* getApplicantAssignmentAutocomplete(
	action: PayloadAction<IPayloadGetApplicantAssignmentAutocompleteRequest>
) {
	const { params } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TGetApplicantAssignmentAutocompleteResponse> =
			yield API.getApplicantAssignmentAutocomplete(params);

		const data = formatApplicantAssignmentAutocomplete(res?.data);

		yield put(
			AssignmentCalendarActions.getApplicantAssignmentAutocompleteSuccess(data)
		);
	} catch (error) {
		yield put(
			AssignmentCalendarActions.getApplicantAssignmentAutocompleteFailure()
		);
	}
}

export function* getFranchiseAssignmentAutocomplete(
	action: PayloadAction<IPayloadGetFranchiseAssignmentAutocompleteRequest>
) {
	const { params } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TGetFranchiseAssignmentAutocompleteResponse> =
			yield API.getFranchiseAssignmentAutocomplete(params);

		const data = formatFranchiseAssignmentAutocomplete(res?.data);

		yield put(
			AssignmentCalendarActions.getFranchiseAssignmentAutocompleteSuccess(data)
		);
	} catch (error) {
		yield put(
			AssignmentCalendarActions.getFranchiseAssignmentAutocompleteFailure()
		);
	}
}
